import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../../api/apiClient";
import RiskDetailDrawer from "../../RiskDetailDrawer";
import Button from "../../ui/Button";
import { typography } from "../../ui/tokens";
import Table from "../../ui/Table";

const SCORE_COLORS = {
  low: "#22c55e",
  medium: "#f59e0b",
  high: "#ef4444",
  neutral: "#94a3b8",
};

const scoreColor = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return SCORE_COLORS.neutral;
  if (value >= 15) return SCORE_COLORS.high;
  if (value >= 8) return SCORE_COLORS.medium;
  return SCORE_COLORS.low;
};

const normalizeCategory = (value) => (value ? value : "Uncategorized");

const getNodeScore = (node) => {
  const candidate =
    node?.residual_score ??
    node?.residual_risk ??
    (node?.residual_likelihood && node?.residual_impact
      ? Number(node.residual_likelihood) * Number(node.residual_impact)
      : null);
  if (candidate === null || candidate === undefined) return null;
  const parsed = Number(candidate);
  return Number.isNaN(parsed) ? null : parsed;
};

const flattenTree = (nodes = []) => {
  const stack = [...nodes];
  const all = [];
  while (stack.length) {
    const node = stack.pop();
    all.push(node);
    if (Array.isArray(node?.children) && node.children.length) {
      stack.push(...node.children);
    }
  }
  return all;
};

export default function RiskInsights() {
  const [rollup, setRollup] = useState([]);
  const [tree, setTree] = useState([]);
  const [rollupLoading, setRollupLoading] = useState(true);
  const [treeLoading, setTreeLoading] = useState(true);
  const [rollupError, setRollupError] = useState(null);
  const [treeError, setTreeError] = useState(null);
  const [selectedRiskId, setSelectedRiskId] = useState(null);
  const handleAddRisk = () => {
    window.dispatchEvent(
      new CustomEvent("navigateModule", {
        detail: { module: "risks", action: "openAddRisk" },
      })
    );
  };

  useEffect(() => {
    let active = true;
    setRollupLoading(true);
    setRollupError(null);
    apiClient
      .get("/risk-dashboard/rollup")
      .then((res) => {
        if (!active) return;
        setRollup(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        if (!active) return;
        setRollupError(err?.message || "Unable to load rollup data.");
      })
      .finally(() => {
        if (!active) return;
        setRollupLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setTreeLoading(true);
    setTreeError(null);
    apiClient
      .get("/risks/tree")
      .then((res) => {
        if (!active) return;
        setTree(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        if (!active) return;
        setTreeError(err?.message || "Unable to load risk tree.");
      })
      .finally(() => {
        if (!active) return;
        setTreeLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const rollupRows = useMemo(() => {
    const flattened = flattenTree(tree);
    const stats = {};

    flattened.forEach((node) => {
      const category = normalizeCategory(node?.category);
      if (!stats[category]) {
        stats[category] = { scoreSum: 0, scoreCount: 0, maxScore: null, nodeCount: 0 };
      }
      stats[category].nodeCount += 1;
      const score = getNodeScore(node);
      if (score !== null) {
        stats[category].scoreSum += score;
        stats[category].scoreCount += 1;
        if (stats[category].maxScore === null || score > stats[category].maxScore) {
          stats[category].maxScore = score;
        }
      }
    });

    const rollupMap = {};
    rollup.forEach((row) => {
      const category = normalizeCategory(row?.category);
      rollupMap[category] = row?.risk_count ?? 0;
    });

    const categories = new Set([...Object.keys(rollupMap), ...Object.keys(stats)]);

    const rows = Array.from(categories).map((category) => {
      const bucket = stats[category] || { scoreSum: 0, scoreCount: 0, maxScore: null, nodeCount: 0 };
      const avgScore = bucket.scoreCount ? bucket.scoreSum / bucket.scoreCount : null;
      const count = rollupMap[category] ?? bucket.nodeCount ?? 0;
      return {
        category,
        count,
        avgScore,
        maxScore: bucket.maxScore,
      };
    });

    return rows.sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [rollup, tree]);

  const rollupColumns = [
    { id: "category", header: "Category", accessor: "category" },
    { id: "count", header: "Risk Count", accessor: "count" },
    {
      id: "avgScore",
      header: "Avg Residual Score",
      accessor: (row) => (row.avgScore === null ? "N/A" : row.avgScore.toFixed(1)),
    },
    {
      id: "maxScore",
      header: "Max Residual Score",
      accessor: (row) => (row.maxScore === null ? "N/A" : row.maxScore.toFixed(1)),
    },
  ];

  return (
    <div style={{ display: "grid", gap: "20px" }}>
      <div>
        <h2 style={{ marginBottom: 6, fontSize: typography.size.xl }}>Risk Rollup & Tree</h2>
        <p style={{ marginTop: 0, color: "#6b7280", fontSize: typography.size.base }}>
          Aggregated risk counts and score insights by category, plus a collapsible hierarchy of parent and child risks.
        </p>
      </div>

      <section style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: typography.size.lg }}>Risk Rollup</h3>
          <div style={{ color: "#6b7280", fontSize: typography.size.xs }}>
            {rollupLoading ? "Loading rollup..." : `${rollupRows.length} categories`}
          </div>
        </div>

        {rollupLoading && <div style={mutedTextStyle}>Loading rollup data...</div>}
        {!rollupLoading && rollupError && (
          <div style={{ ...mutedTextStyle, color: "#b91c1c" }}>Error: {rollupError}</div>
        )}
        {!rollupLoading && !rollupError && rollupRows.length === 0 && (
          <div style={emptyStateStyle}>
            <div style={emptyTitleStyle}>No rollup data yet</div>
            <div style={emptyBodyStyle}>Risk rollups are generated once risks are added.</div>
            <Button onClick={handleAddRisk} variant="primary">
              Add your first risk
            </Button>
          </div>
        )}
        {!rollupLoading && !rollupError && rollupRows.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <Table
              columns={rollupColumns}
              data={rollupRows}
              getRowKey={(row) => row.category}
              emptyState="No rollup data yet."
            />
          </div>
        )}
      </section>

      <section style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: typography.size.lg }}>Risk Tree</h3>
          <div style={{ color: "#6b7280", fontSize: typography.size.xs }}>
            {treeLoading ? "Loading tree..." : `${tree.length} root risks`}
          </div>
        </div>

        {treeLoading && <div style={mutedTextStyle}>Loading risk tree...</div>}
        {!treeLoading && treeError && (
          <div style={{ ...mutedTextStyle, color: "#b91c1c" }}>Error: {treeError}</div>
        )}
        {!treeLoading && !treeError && tree.length === 0 && (
          <div style={emptyStateStyle}>
            <div style={emptyTitleStyle}>No risks in the tree yet</div>
            <div style={emptyBodyStyle}>Add a risk to generate the hierarchy view.</div>
            <Button onClick={handleAddRisk} variant="primary">
              Add your first risk
            </Button>
          </div>
        )}
        {!treeLoading && !treeError && tree.length > 0 && (
          <div style={{ display: "grid", gap: "6px" }}>
            {tree.map((node, index) => (
              <RiskTreeNode
                key={`${node?.id ?? "risk"}-root-${index}`}
                node={node}
                depth={0}
                path={`root-${index}`}
                onSelect={setSelectedRiskId}
              />
            ))}
          </div>
        )}
      </section>

      <RiskDetailDrawer riskId={selectedRiskId} onClose={() => setSelectedRiskId(null)} />
    </div>
  );
}

function RiskTreeNode({ node, depth, path, onSelect }) {
  const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
  const [expanded, setExpanded] = useState(depth < 1);
  const score = getNodeScore(node);
  const scoreLabel = score === null ? "N/A" : score.toFixed(1);

  return (
    <div>
      <div style={{ ...treeRowStyle, marginLeft: depth * 18 }}>
        <Button
          type="button"
          onClick={() => hasChildren && setExpanded((prev) => !prev)}
          variant="ghost"
          style={{
            ...treeToggleStyle,
            cursor: hasChildren ? "pointer" : "default",
            opacity: hasChildren ? 1 : 0.35,
          }}
          aria-label={hasChildren ? "Toggle children" : "Leaf node"}
        >
          {hasChildren ? (expanded ? "-" : "+") : "*"}
        </Button>
        <Button
          type="button"
          onClick={() => onSelect?.(node.id)}
          variant="ghost"
          style={treeNameButtonStyle}
        >
          {node?.name || "Untitled risk"}
        </Button>
        <span style={treeMetaStyle}>{node?.category || "Uncategorized"}</span>
        <span style={treeMetaStyle}>{node?.status || "Unknown status"}</span>
        <span style={{ ...scoreBadgeStyle, background: scoreColor(score) }}>{scoreLabel}</span>
      </div>

      {hasChildren && expanded && (
        <div style={{ marginTop: 4 }}>
          {node.children.map((child, index) => (
            <RiskTreeNode
              key={`${child?.id ?? "risk"}-${path}-${index}`}
              node={child}
              depth={depth + 1}
              path={`${path}-${index}`}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const sectionStyle = {
  background: "#ffffff",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: "16px",
  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.04)",
};

const sectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 12,
};

const mutedTextStyle = {
  fontSize: typography.size.sm,
  color: "#6b7280",
};

const emptyStateStyle = {
  display: "grid",
  gap: 8,
  padding: "12px",
  borderRadius: 10,
  border: "1px dashed #d1d5db",
  background: "#f9fafb",
};

const emptyTitleStyle = {
  fontWeight: typography.weight.medium,
  color: "#111827",
};

const emptyBodyStyle = {
  fontSize: typography.size.sm,
  color: "#6b7280",
};

const treeRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "6px 8px",
  borderRadius: 8,
  border: "1px solid #eef2f7",
  background: "#f9fafb",
};

const treeToggleStyle = {
  width: 24,
  height: 24,
  borderRadius: 6,
  border: "1px solid #d1d5db",
  background: "#ffffff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: typography.weight.bold,
  color: "#111827",
};

const treeNameButtonStyle = {
  border: "none",
  background: "transparent",
  color: "#0f766e",
  fontWeight: typography.weight.medium,
  cursor: "pointer",
  padding: 0,
};

const treeMetaStyle = {
  fontSize: typography.size.xs,
  color: "#6b7280",
  padding: "2px 6px",
  borderRadius: 999,
  background: "#f1f5f9",
};

const scoreBadgeStyle = {
  marginLeft: "auto",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.bold,
  color: "#ffffff",
  padding: "2px 8px",
  borderRadius: 999,
};
